import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import routes from "../../routes";
import LegacyRedirect from "../Routing/LegacyRedirect";
import NotFound from "../../Views/NotFound/NotFound";
import Sidebar from "../Sidebar";
import { MainNav } from "../MuiTable";
import "./style.css";
import "./admin-dark-overrides.css";
import "./donor-dark.css";
import { Grid } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
// JOB-7066: merchant branding (palette for the viewer's appearance, fonts).
import { BrandContext, useCompanyBrand } from "../../contexts/BrandContext";
import { withBrandTheme } from "../../utils/paintBrand";
import {
  AdminDarkModeProvider,
  useAdminDarkMode,
} from "../../contexts/AdminDarkModeContext";
import { updateCompanyNameFromToken, setPageTitle, getPageTitleFromRoute } from "../../utils/pageTitle";
import { prefetchAdminRoutes } from "../../utils/prefetchAdminRoutes";
import {
  CJ_PORTAL_CONTENT_OFFSET_COLLAPSED_PX,
  CJ_PORTAL_CONTENT_OFFSET_EXPANDED_PX,
} from "../../utils/cjPortalSidebarMetrics";
import { AdminPageSkeleton } from "../Loading/AdminDataSkeleton";
// JOB-5706: the admin shell needs to expose donation mode to CSS. Several dark
// rules have to differ between CDM and contractor, and until now the shell only
// carried .admin-shell--dark, so a stylesheet had nothing to branch on and had
// to pick one product's colour for both.
import { useUserType } from "../../contexts/UserTypeContext";
// LIMO: the product vertical. useProduct() rather than extending useUserType —
// UserTypeContext is a legacy shim over this very hook and its own header says
// new code should read `mode` / `isLimo` here.
import { useProduct } from "../../contexts/ProductContext";
import useLimoTheme from "../../hooks/useLimoTheme";
import useShellScrollLock from "../../hooks/useShellScrollLock";
import { applyColorScheme } from "../../utils/colorScheme";

const MOBILE_MQ = "(max-width: 767px)";

function subscribeAdminMobileLayout(callback) {
  if (typeof window === "undefined") return () => {};
  const mq = window.matchMedia(MOBILE_MQ);
  mq.addEventListener("change", callback);
  return () => mq.removeEventListener("change", callback);
}

function getAdminMobileLayoutSnapshot() {
  if (typeof window === "undefined") return false;
  return window.matchMedia(MOBILE_MQ).matches;
}

/** Assume narrow viewport on SSR so HTML is not emitted with desktop sidebar offset. */
function getAdminMobileLayoutServerSnapshot() {
  return true;
}

function AdminInner() {
  const { darkMode } = useAdminDarkMode();
  const brand = useCompanyBrand(darkMode);
  // JOB-5706: host-derived, same source the rest of the shell brands from.
  const { isDonor: isDonationMode } = useUserType();
  const { isLimo } = useProduct();
  const mainContent = useRef(null);
  const scrollShell = useRef(null);
  const { CompanyUrl } = useParams();
  const location = useLocation();
  const [isSidebarClosed, setIsSidebarClosed] = useState(false);
  const [isSidebarDisplay, setIsSidebarDisplay] = useState(true);
  /* useMediaQuery can report “desktop” on the first client paint → inline
     marginLeft: 260px briefly (or stuck), which matches the empty-left / shifted
     content bug on phones. useSyncExternalStore reads matchMedia synchronously. */
  const isMobileLayout = useSyncExternalStore(
    subscribeAdminMobileLayout,
    getAdminMobileLayoutSnapshot,
    getAdminMobileLayoutServerSnapshot
  );

  // Reset sidebar state when screen size changes
  useEffect(() => {
    if (!isMobileLayout) {
      setIsSidebarDisplay(true);
      setIsSidebarClosed(false);
    } else {
      setIsSidebarDisplay(false);
    }
  }, [isMobileLayout]);

  // Background-warm the chunks for the top-volume admin pages (Customer,
  // Quotes, Contracts, Invoices, Service Requests, Schedule, Today's
  // Appointments, POS) once the dashboard has rendered. By the time the
  // user clicks a sidebar link the chunk is already in the module cache,
  // so navigation feels instant instead of waiting on Vite to lazy-load
  // and transform the page. Defers via requestIdleCallback to never
  // compete with the first-paint critical path.
  useEffect(() => {
    prefetchAdminRoutes();
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add("admin-dark-active");
    } else {
      document.body.classList.remove("admin-dark-active");
    }
    // LIMO-205: <html> has to say the same thing the page looks like. The
    // browser reads color-scheme for native controls and scrollbars, and CSS
    // reads the attribute; index.html sets both to light before React boots
    // and nothing used to update them afterwards.
    applyColorScheme(darkMode);
    return () => {
      document.body.classList.remove("admin-dark-active");
      applyColorScheme(false);
    };
  }, [darkMode]);

  // JOB-5706: mirror of the shell class on <body>, because roughly half the
  // dark rules key on `body.admin-dark-active` (they have to — MUI portals
  // menus, poppers and dialogs to document.body, outside .admin-shell). The
  // donation-mode branch has to be reachable from both scopes or it only works
  // for half the controls. Companion to `body.donor-dark-active` (JOB-5687).
  useEffect(() => {
    if (isDonationMode) {
      document.body.classList.add("admin-donation-mode");
    } else {
      document.body.classList.remove("admin-donation-mode");
    }
    return () => document.body.classList.remove("admin-donation-mode");
  }, [isDonationMode]);

  // LIMO: the brand-token scope, companion to the donation-mode class above and
  // for the same reason — half the chrome is portaled to <body> by MUI, so a
  // class on the shell alone would theme only the other half. index.css keys the
  // Cloud Limo Manager palette off `.admin-shell--limo, body.admin-limo-mode`,
  // and its dark block off `body.admin-dark-active.admin-limo-mode`.
  const limoShell = useLimoTheme(isLimo);

  // JOB-7437 / JOB-7517: the shell is the only scroller, so the document itself must not scroll.
  useShellScrollLock();

  useEffect(() => {
    // JOB-5088: document.documentElement/scrollingElement are no-ops here —
    // the actual scroll container is the outer 100vh/overflow:auto
    // `.admin-shell` Grid below, not the inner `mainContent` ref (which has
    // no scroll context of its own). Reset the real scroller.
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mainContent.current) {
      mainContent.current.scrollTop = 0;
    }
    if (scrollShell.current) {
      scrollShell.current.scrollTop = 0;
    }

    // JOB-5318's Edit/Add-User refinement now lives inside
    // getPageTitleFromRoute itself (see pageTitle.js) so this call and
    // ProtectedRoute's identical one can no longer disagree.
    setPageTitle(getPageTitleFromRoute(location?.pathname, location?.state));
  }, [location]);

  const getRoutes = (routes) => {
    return routes?.map((prop, key) => {
      if (prop.layout === `/:CompanyUrl` || prop.layout === `/${CompanyUrl}`) {
        if (prop?.children) {
          return prop?.children?.map((item, index) => (
            <Route
              path={`${item?.path}`}
              element={item?.component}
              key={index}
            />
          ));
        } else {
          return <Route path={prop?.path} element={prop?.component} key={key} />;
        }
      }
      return null;
    });
  };

  const adminTheme = useMemo(
    () =>
      createTheme(
        withBrandTheme({
          palette: {
            mode: darkMode ? "dark" : "light",
            primary: {
              main: darkMode ? "#90caf9" : "#1976d2", // brand-exempt: MUI palette value (needs a parseable color); brand applied via withBrandTheme
            },
            secondary: {
              main: darkMode ? "#f48fb1" : "#dc004e",
            },
            background: {
              default: darkMode ? "#0f1419" : "#f7f8fa",
              paper: darkMode ? "#1a2332" : "#ffffff",
            },
            ...(darkMode
              ? {
                  text: {
                    primary: "#e8eaed",
                    secondary: "#9aa0a6",
                  },
                  divider: "#2d3b4e",
                }
              : {}),
          },
          components: {
            MuiPaper: {
              styleOverrides: {
                root: {
                  backgroundImage: "none",
                },
              },
            },
          },
          typography: {
            fontFamily: "'Poppins', sans-serif",
            h1: {
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 600,
            },
            h2: {
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 500,
            },
            body1: {
              fontFamily: "'Poppins', sans-serif",
            },
          },
        }, brand.data, darkMode)
      ),
    [darkMode, brand.data]
  );

  return (
    <BrandContext.Provider value={brand}>
    <ThemeProvider theme={adminTheme}>
    <Grid
      ref={scrollShell}
      className={`admin-shell${darkMode ? " admin-shell--dark" : ""}${
        isDonationMode ? " admin-shell--donation" : ""
      }${limoShell}`}
      style={{
        backgroundColor: "var(--app-page-background)",
        // JOB-7437 / JOB-7517: width and height come from `.admin-shell` in index.css
        // (100% wide, not 100vw, which counts the window scrollbar; 100dvh with a 100vh fallback).
        overflow: "auto",
        overflowX: "hidden",
      }}
    >
      <Sidebar
        layout={`/:CompanyUrl`}
        isSidebarClosed={isSidebarClosed}
        setIsSidebarClosed={setIsSidebarClosed}
        isSidebarDisplay={isSidebarDisplay}
        setIsSidebarDisplay={setIsSidebarDisplay}
      />
      <MainNav
        layout="/:CompanyUrl"
        isSidebarClosed={isSidebarClosed}
        isSidebarDisplay={isSidebarDisplay}
        setIsSidebarDisplay={setIsSidebarDisplay}
      />
      <Grid
        className="main-content invoicePaidSlip"
        style={
          isMobileLayout
            ? {
                marginLeft: 0,
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                paddingLeft: 15,
                paddingRight: 15,
                boxSizing: "border-box",
                // JOB-4289: scope the transition to margin-left (the only
                // property that changes, on sidebar collapse). The previous
                // `all` animated every property change — including layout
                // shifts during route changes / lazy-chunk swaps — which could
                // produce the transient "ghosted" double-render seen on
                // navigation. Mitigation; the intermittent jank is also tied
                // to navigation perf (JOB-4270).
                transition: "margin-left 0.3s ease",
              }
            : {
                // JOB-5510: was the raw strings "100px" / "260px". Identical
                // values, now read from the shared constants — this file was the
                // only definition of the sidebar clearance, which is how the
                // superadmin and staff layouts ended up flush against the rail.
                marginLeft: isSidebarClosed
                  ? `${CJ_PORTAL_CONTENT_OFFSET_COLLAPSED_PX}px`
                  : `${CJ_PORTAL_CONTENT_OFFSET_EXPANDED_PX}px`,
                width: "auto",
                paddingLeft: 0,
                paddingRight: 0,
                // JOB-4289: scope the transition to margin-left (the only
                // property that changes, on sidebar collapse). The previous
                // `all` animated every property change — including layout
                // shifts during route changes / lazy-chunk swaps — which could
                // produce the transient "ghosted" double-render seen on
                // navigation. Mitigation; the intermittent jank is also tied
                // to navigation perf (JOB-4270).
                transition: "margin-left 0.3s ease",
              }
        }
        ref={mainContent}
      >
        <Grid
          style={{
            width: isMobileLayout ? "100%" : "98%",
            marginLeft: "auto",
            marginRight: "auto",
            minWidth: 0,
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
          className="customerDetail_paid"
        >
          {/* JOB-4026: show a skeleton (not a blank white area) while a
              lazy route chunk loads. Without this the content area painted
              empty for several seconds on first navigation to un-warmed
              pages (Contracts/Invoices/Add-Quote), reading as frozen. The
              skeleton is dark-mode aware and matches the Schedule page. */}
          <React.Suspense fallback={<AdminPageSkeleton minHeight="60vh" />}>
            <Routes>
              {getRoutes(routes)}
              {/* JOB-3225: singular/plural aliases so common typos reach the
                  canonical URL instead of a silent redirect to /index. */}
              <Route path="/quote" element={<Navigate to={`/${CompanyUrl}/quotes`} replace />} />
              <Route path="/contracts" element={<Navigate to={`/${CompanyUrl}/contract`} replace />} />
              <Route path="/invoices" element={<Navigate to={`/${CompanyUrl}/invoice`} replace />} />
              <Route path="/materials" element={<Navigate to={`/${CompanyUrl}/products-services`} replace />} />
              {/* JOB-6931: the page moved to /products-services; bookmarks,
                  saved links and old notifications keep working. */}
              <Route path="/materials-labor" element={<LegacyRedirect to={`/${CompanyUrl}/products-services`} />} />
              {/* JOB-3220 + JOB-3225: real 404 for anything that doesn't match.
                  No more silent /index redirect that leaves the typed URL in the
                  address bar with the dashboard content. */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </React.Suspense>
        </Grid>
      </Grid>
    </Grid>
    </ThemeProvider>
    </BrandContext.Provider>
  );
}

const Admin = () => (
  <AdminDarkModeProvider>
    <AdminInner />
  </AdminDarkModeProvider>
);

export default Admin;
